/* Terms & Conditions , static, cream theme matching Privacy/FAQ/Contact.
   Real Magic Cruises operational rules (board times, weather, conduct, capacity)
   from cruiseData.js + magiccruises.com.au public policy. */
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, ArrowRight, FileText } from "lucide-react";
import { CONTACT } from "../data/cruiseData";
import SEOHead from "../components/SEOHead";

const SECTIONS = [
 {
  id: "agreement",
  title: "Your agreement with us",
  body: [
   "These Terms & Conditions form the legal agreement between you (the customer) and Magic Cruises Pty Ltd (the operator) when you enquire about a cruise, book a private charter, purchase a ticketed event or board the MV Sydney Magic.",
   "By submitting an enquiry, paying a deposit or purchasing a ticket you confirm that you have read, understood and accepted these terms. If you are booking on behalf of a group, you confirm you have authority to accept these terms on their behalf and you are responsible for communicating them to your guests.",
  ],
 },
 {
  id: "bookings",
  title: "Bookings and quotes",
  body: [
   "Cruise quotes are valid for 14 days from the date issued. After 14 days, pricing may change due to seasonal variation, public holiday surcharges or fuel/operating cost changes. We confirm a final, locked price when we receive your deposit.",
   "Bookings are not confirmed until we receive a signed booking confirmation and the required deposit. Provisional date holds expire after 7 days unless secured by deposit.",
   "Ticketed cruises (Vivid, NYE, Australia Day, Boxing Day and similar published events) are confirmed at the point of purchase through Stripe Checkout. Tickets are non-refundable but transferable in line with the ticket transfer policy below.",
  ],
 },
 {
  id: "deposits",
  title: "Deposits and final payment",
  body: [
   "Private charter bookings require a 25% deposit to secure the date. The deposit is non-refundable except where Magic Cruises cancels the cruise (see Cancellation by Magic Cruises below).",
   "The remaining balance is due 14 days before the cruise date. For bookings made within 14 days of departure the full amount is payable at the time of booking.",
   "Public holiday charters carry a +20% surcharge on the total booking with a 4-hour minimum charter duration.",
  ],
 },
 {
  id: "cancellation",
  title: "Cancellation by you",
  body: [
   "More than 60 days before the cruise: deposit is forfeited, balance not yet due.",
   "30 to 60 days before the cruise: deposit forfeited, 25% of the balance forfeited.",
   "14 to 30 days before the cruise: deposit forfeited, 50% of the balance forfeited.",
   "Less than 14 days before the cruise: 100% of the booking is forfeited.",
   "Cancellations must be submitted in writing to info@magiccruises.com.au. Time of cancellation is the time we receive the email.",
   "Ticketed event tickets are non-refundable. Tickets may be transferred to another guest at no charge up to 48 hours before boarding by emailing us the new guest name.",
  ],
 },
 {
  id: "cancellation-by-us",
  title: "Cancellation by Magic Cruises",
  body: [
   "On rare occasions we may cancel a cruise due to unsafe weather, mechanical fault, marine authority direction or other circumstances beyond our reasonable control. We make the call no later than 4 hours before scheduled boarding and contact the lead booker by phone and email.",
   "When we cancel a cruise we offer (at your choice): a full refund of monies paid, a credit to the same value valid for 12 months, or a transfer to an alternative date subject to availability.",
   "We are not liable for incidental expenses (transport, accommodation, supplier deposits) arising from a Magic Cruises cancellation. We recommend customers arrange their own event insurance for high-value bookings.",
  ],
 },
 {
  id: "weather",
  title: "Weather policy",
  body: [
   "The MV Sydney Magic is a fully enclosed, climate-controlled vessel and operates in almost all Sydney Harbour conditions. Cruises proceed in rain, wind and overcast weather.",
   "We cancel only when sea state, lightning, fire or marine authority direction makes operations unsafe. The captain's decision is final and is made in accordance with NSW Maritime safety guidelines.",
   "If conditions deteriorate during a cruise the captain may shorten the route or return early. No refunds or credits are given for shortened cruises where the booked duration was operated safely up to the point of return.",
  ],
 },
 {
  id: "boarding",
  title: "Boarding, departure and disembarkation",
  body: [
   `Boarding is at King Street Wharf, Darling Harbour (the exact pontoon is confirmed in your booking pack). Boarding opens 15 minutes before the scheduled departure time.`,
   "The boat departs on time. Late arrivals may miss the cruise; no refunds are issued for missed departures. We strongly recommend arriving 30 minutes early to allow for parking and walk-down.",
   "Photo ID is required for ticketed events serving alcohol. Children attending family cruises must be supervised by a parent or guardian at all times.",
  ],
 },
 {
  id: "capacity",
  title: "Vessel capacity and guest count",
  body: [
   "The MV Sydney Magic is licensed for a maximum of 120 guests cocktail-style or 40 seated. The final guest count must be confirmed in writing 14 days before the cruise; this number determines the catering and bar setup.",
   "Additional guests beyond the confirmed count may be charged at the prevailing per-head rate and are subject to capacity limits enforced by NSW Maritime. We may refuse boarding if the total guest count exceeds the licensed capacity.",
   "Final headcount changes within 7 days of the cruise may not be reflected in catering quantities, but no refund is given for guests who do not attend.",
  ],
 },
 {
  id: "conduct",
  title: "Conduct on board",
  body: [
   "All guests must follow the directions of the captain, crew and bar staff. The crew has full authority to refuse service, restrict access to areas of the vessel or terminate the cruise where guest behaviour creates a safety, legal or operational risk.",
   "Unacceptable conduct includes (but is not limited to): intoxication that endangers safety, illegal drug use, violence, harassment, climbing on railings, jumping overboard, or interfering with vessel equipment. Where the cruise is terminated due to guest misconduct, no refund is given.",
   "All cruises serving alcohol are operated under Responsible Service of Alcohol guidelines. Underage drinking is strictly prohibited and the boat carries breath-testing equipment used at the captain's discretion.",
   "Smoking and vaping are not permitted anywhere on board. Designated guests may smoke on the wharf before boarding or during a wharf return only.",
  ],
 },
 {
  id: "age-bucks-hens",
  title: "Age limits, bucks and hens cruises",
  body: [
   "Public ticketed events (Vivid, NYE, Australia Day, Boxing Day) require all attendees to be 18+. Photo ID is checked at boarding.",
   "Private charters allow under-18 guests where supervised by a parent or guardian. Under-21 events (school formals, 18ths) require a licensed security guard at $350 for up to 4 hours.",
   "Bucks and hens cruises are accepted on full charter basis only, are not permitted on BYO Package 2 (food only), and must accept reasonable Responsible Service of Alcohol enforcement by the bar staff.",
  ],
 },
 {
  id: "byo",
  title: "BYO food and beverage",
  body: [
   "Where the BYO package permits, customers may bring their own food and (depending on package) beverages. BYO drinks must be arranged as a single delivery to the vessel before boarding; individual guests cannot bring drinks separately.",
   "BYO food must be sufficient for the guest count and presented in a serviceable format (platters, sealed containers). The crew cannot heat, plate or serve BYO food beyond a basic table setup.",
   "Glassware, ice, cutlery and rubbish removal are the responsibility of the customer unless explicitly included in the package. A rubbish disposal fee of $200 applies where guests do not remove their own rubbish at the end of the cruise.",
  ],
 },
 {
  id: "damage",
  title: "Damage, loss and lost property",
  body: [
   "The lead booker is responsible for any wilful or negligent damage to the vessel, fixtures or fittings caused by guests during the cruise. We invoice for repair costs at standard marine repair rates.",
   "Personal property (phones, jackets, gifts) is the responsibility of guests. We are not liable for items lost, stolen or damaged on board. Items left on the vessel after disembarkation are held at our office for 14 days then disposed of.",
  ],
 },
 {
  id: "liability",
  title: "Liability",
  body: [
   "To the extent permitted by Australian Consumer Law, Magic Cruises' total liability for any single booking is limited to the total amount paid to us for that booking.",
   "We are not liable for indirect, incidental or consequential loss (including but not limited to loss of profit, loss of opportunity, supplier deposits or transport costs).",
   "Nothing in these terms excludes, restricts or modifies your rights under the Australian Consumer Law (Schedule 2 of the Competition and Consumer Act 2010 (Cth)) where those rights cannot be excluded by law.",
  ],
 },
 {
  id: "privacy",
  title: "Privacy",
  body: [
   "Personal information collected as part of your booking is handled in accordance with our Privacy Policy. Please review it before submitting an enquiry or purchasing a ticket.",
  ],
 },
 {
  id: "governing-law",
  title: "Governing law",
  body: [
   "These Terms & Conditions are governed by the laws of New South Wales, Australia. Any dispute arising out of or in connection with these terms is subject to the exclusive jurisdiction of the courts of New South Wales.",
   "If any part of these terms is found to be unenforceable, the remaining provisions continue in full force.",
  ],
 },
 {
  id: "contact",
  title: "Contact",
  body: [
   "Questions about a booking, a quote, a refund or these terms should be directed to Magic Cruises by phone (1300 100 502) or email (info@magiccruises.com.au).",
  ],
 },
];


export default function Terms() {
 useEffect(() => { document.title = "Terms & Conditions | Magic Cruises Sydney"; }, []);
 return (
  <main className="pt-32 pb-24" data-testid="terms-page">
   <SEOHead title="Terms & Conditions | Magic Cruises Sydney" description="Magic Cruises booking, cancellation and conduct terms for private charter cruises and ticketed events on Sydney Harbour." canonical="https://magiccruises.com.au/terms" />

   <div className="mx-auto max-w-3xl px-5 lg:px-10">
    <div className="eyebrow inline-flex items-center gap-2"><FileText className="w-3.5 h-3.5 text-pink" /> Legal</div>
    <h1 className="font-display text-4xl lg:text-7xl mt-3 mb-5 text-ink leading-[1]" data-testid="terms-h1">
     Terms & <span className="text-pink italic">Conditions</span>.
    </h1>
    <p className="text-ink-soft text-base lg:text-lg leading-relaxed mb-2">
     Plain-English summary of the booking, payment, cancellation and on-board rules that apply to every cruise on the MV Sydney Magic.
    </p>
    <p className="text-ink-soft/70 text-xs uppercase tracking-[0.22em] font-bold mb-12">Last updated · February 2026</p>

    {/* TOC */}
    <nav aria-label="Terms sections" className="mb-12 rounded-2xl border border-[hsla(222,20%,80%,0.5)] bg-white/60 p-5" data-testid="terms-toc">
     <div className="text-[10px] uppercase tracking-[0.28em] font-black text-ink-soft mb-3">On this page</div>
     <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
      {SECTIONS.map((s) => (
       <li key={s.id}>
        <a href={`#${s.id}`} className="text-ink hover:text-pink transition-colors inline-flex items-center gap-1.5 group" data-testid={`terms-toc-${s.id}`}>
         <ArrowRight className="w-3 h-3 opacity-40 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" /> {s.title}
        </a>
       </li>
      ))}
     </ul>
    </nav>

    <div className="space-y-12">
     {SECTIONS.map((s) => (
      <section key={s.id} id={s.id} className="scroll-mt-32" data-testid={`terms-section-${s.id}`}>
       <h2 className="font-display text-2xl lg:text-3xl text-ink mb-4 leading-tight">{s.title}</h2>
       {s.body.map((p, i) => (
        <p key={i} className="text-ink-soft text-base leading-relaxed mb-3">{p}</p>
       ))}
      </section>
     ))}
    </div>

    {/* Contact block */}
    <div className="mt-16 rounded-2xl bg-white border border-[hsla(222,20%,80%,0.5)] p-7 lg:p-9" data-testid="terms-contact">
     <div className="eyebrow mb-3">Need a hand?</div>
     <h3 className="font-display text-2xl lg:text-3xl text-ink mb-5">Talk to the team.</h3>
     <div className="space-y-3 text-sm font-medium">
      <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 text-ink hover:text-pink"><Mail className="w-4 h-4 text-pink" /> {CONTACT.email}</a>
      <a href={`tel:${CONTACT.phone1}`} className="flex items-center gap-3 text-ink hover:text-pink"><Phone className="w-4 h-4 text-pink" /> {CONTACT.phone1}</a>
     </div>
     <div className="mt-6 pt-6 border-t border-[hsla(222,20%,80%,0.5)] flex flex-wrap gap-4 text-sm">
      <Link to="/privacy" className="text-pink hover:text-pink/80 font-bold inline-flex items-center gap-1" data-testid="terms-to-privacy">Privacy Policy <ArrowRight className="w-3 h-3" /></Link>
      <Link to="/contact" className="text-ink hover:text-pink font-bold inline-flex items-center gap-1" data-testid="terms-to-contact">Contact <ArrowRight className="w-3 h-3" /></Link>
     </div>
    </div>
   </div>
  </main>
 );
}
